import { defaultTheme, Provider } from "@adobe/react-spectrum";
import { Canvas } from "@react-three/fiber";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Vector3, SRGBColorSpace, NoToneMapping } from 'three';
import "./css/App.css";
import { cameraManager } from "./js/Camera";
import { IS_UNDER_CONSTRUCTION, version } from "./js/Constants";
import { getNextYRotation, getNextFaceRotation, getRotationLabel } from "./js/blocks/BlockRotations";
import EnvironmentBuilder, { environmentModels } from "./js/EnvironmentBuilder";
import TerrainBuilder from "./js/TerrainBuilder";
import { BlockToolOptions } from "./js/components/BlockToolOptions";
import BlockToolsSidebar, {
    refreshBlockTools,
} from "./js/components/BlockToolsSidebar";
import GlobalLoadingScreen from "./js/components/GlobalLoadingScreen";
import QuickTips from './js/components/QuickTips';
import ToastManager from './js/components/ToastManager';
import TextureGenerationModal from "./js/components/TextureGenerationModal";
import SchematicNameModal from "./js/components/SchematicNameModal";
import SelectionDimensionsTip from "./js/components/SelectionDimensionsTip";
import ToolBar from "./js/components/ToolBar";
import UnderConstruction from "./js/components/UnderConstruction";
import ProjectHome from "./js/components/ProjectHome";
import {
    blockTypes,
    getCustomBlocks,
    processCustomBlock,
    removeCustomBlock,
    updateCustomBlockName,
} from "./js/managers/BlockTypesManager";
import { DatabaseManager, STORES } from "./js/managers/DatabaseManager";
import { loadingManager } from "./js/managers/LoadingManager";
import UndoRedoManager from "./js/managers/UndoRedoManager";
import PhysicsManager from './js/physics/PhysicsManager';
import { detectGPU, getOptimalContextAttributes } from "./js/utils/GPUDetection";
import { updateChunkSystemCamera, processChunkRenderQueue, getChunkSystem } from "./js/chunks/TerrainBuilderIntegration";
import { createPlaceholderBlob, dataURLtoBlob } from "./js/utils/blobUtils";
import { generateThumbnail } from "./js/utils/thumbnailUtils";
import { isElectronRuntime } from './js/utils/env';
import { getHytopiaBlocks } from "./js/utils/minecraft/BlockMapper";
import { convertComponentToGLTF } from "./js/utils/ComponentToEntityConverter";
import PostProcessingManager from "./js/components/PostProcessingManager";
import { zoneManager } from "./js/managers/ZoneManager";
import { importMap } from "./js/ImportExport";

function App() {
    // Project state
    // Always start at Project Home; don't auto-open last project
    const [projectId, setProjectId] = useState<string | null>(null);

    // Log initialization only once
    useEffect(() => {
        return () => {
            // Only mark end if we're actually unmounting (not just StrictMode re-render)
            // We'll mark end when pageIsLoaded is set instead
        };
    }, []); // Empty deps - only run once

    useEffect(() => {
        if (projectId) {
            DatabaseManager.setCurrentProjectId(projectId);
        }
    }, [projectId]);

    // Listen for toast notifications
    useEffect(() => {
        const handleToastChange = (toastList: Array<{ id: string; message: string; timestamp: number }>) => {
            setToasts([...toastList]);
        };
        ToastManager.addListener(handleToastChange);
        return () => {
            ToastManager.removeListener(handleToastChange);
        };
    }, []);
    const handleSwitchProject = async () => {
        try {
            const shouldSave = window.confirm("Switch projects? Save current project before switching?");
            if (shouldSave) {
                try {
                    if (terrainBuilderRef.current) {
                        await terrainBuilderRef.current.saveTerrainManually();
                        const url = await generateWorldThumbnail();
                        if (url) {
                            const pid = DatabaseManager.getCurrentProjectId();
                            await DatabaseManager.saveProjectThumbnail(pid, url);
                        }
                    }
                    if (environmentBuilderRef.current) {
                        await environmentBuilderRef.current.updateLocalStorage();
                    }
                } catch (_) { }
            }
        } finally {
            try { localStorage.removeItem("CURRENT_PROJECT_ID"); } catch (_) { }
            setProjectId(null);
            // Ensure DB manager does not keep stale project context
            try { DatabaseManager.setCurrentProjectId(null as any); } catch (_) { }
        }
    };
    const undoRedoManagerRef = useRef(null);
    const lastResetProjectIdRef = useRef<string | null>(null);
    const [currentBlockType, setCurrentBlockType] = useState(blockTypes[0]);
    const [currentRotationIndex, setCurrentRotationIndex] = useState(0);
    const [currentShapeType, setCurrentShapeType] = useState('cube');
    const [mode, setMode] = useState("add");
    const [axisLockEnabled, setAxisLockEnabled] = useState(false);
    const [cameraReset, setCameraReset] = useState(false);
    const [placementSize, setPlacementSize] = useState("single");
    const [activeTab, setActiveTab] = useState("blocks");
    const [pageIsLoaded, setPageIsLoaded] = useState(false);
    const [scene, setScene] = useState(null);
    const [totalEnvironmentObjects, setTotalEnvironmentObjects] = useState(0);
    const [currentPreviewPosition, setCurrentPreviewPosition] = useState(null);
    const environmentBuilderRef = useRef(null);
    const terrainBuilderRef = useRef(null);
    const [placementSettings, setPlacementSettings] = useState({
        randomScale: false,
        randomRotation: false,
        minScale: 0.5,
        maxScale: 1.5,
        minRotation: 0,
        maxRotation: 360,
        scale: 1.0,
        rotation: 0,
        snapToGrid: true,
    });
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'complete'>('idle');
    const [toasts, setToasts] = useState<Array<{ id: string; message: string; timestamp: number }>>([]);
    const [isTextureModalOpen, setIsTextureModalOpen] = useState(false);
    const [isAIComponentsActive, setIsAIComponentsActive] = useState(false);
    const [showBlockSidebar, setShowBlockSidebar] = useState(true);
    const [showOptionsPanel, setShowOptionsPanel] = useState(true);
    const [showToolbar, setShowToolbar] = useState(true);
    const [isCompactMode, setIsCompactMode] = useState(true);
    const [cameraPosition, setCameraPosition] = useState(null);
    const [playerModeEnabled, setPlayerModeEnabled] = useState(false);
    const lastBaseGridYRef = useRef<number | null>(null);
    const physicsRef = useRef<PhysicsManager | null>(null);
    const [bloomEnabled, setBloomEnabled] = useState(true); // Enabled by default for emissive glow
    const [ambientLightIntensity, setAmbientLightIntensity] = useState(1.0); // Track ambient light for bloom threshold
    const cameraAngle = 0;
    const gridSize = 5000;

    // Initialize GPU detection and optimized context attributes (only log once)
    const gpuInfo = useMemo(() => {
        const info = detectGPU();
        return info;
    }, []); // Only compute once

    const contextAttributes = useMemo(() => getOptimalContextAttributes(gpuInfo), [gpuInfo]);

    useEffect(() => {
        if (terrainBuilderRef.current) {
            terrainBuilderRef.current.updateGridSize(gridSize);
        }
    }, [gridSize, terrainBuilderRef.current?.updateGridSize]);

    // Capture a thumbnail from a reset camera angle, then restore camera
    const generateWorldThumbnail = async (): Promise<string | null> => {
        try {
            const container = document.querySelector('.canvas-container') as HTMLElement | null;
            const canvas: any = (container && container.querySelector('canvas')) || null;
            if (!canvas || typeof canvas.toDataURL !== 'function') return null;
            const prev = cameraManager.getCameraState?.() || null;
            let prevJson: string | null = null;
            try { prevJson = prev ? JSON.stringify(prev) : null; } catch (_) { prevJson = null; }
            // Hide preview/highlight UI while capturing
            let oldPreviewVisible: boolean | undefined;
            try {
                const tb = terrainBuilderRef.current;
                if (tb && tb.previewPositionRef !== undefined) {
                    oldPreviewVisible = (window as any).__WE_PREVIEW_VISIBLE__;
                    (window as any).__WE_PREVIEW_VISIBLE__ = false;
                }
            } catch (_) { }
            try { cameraManager.resetCamera?.(); } catch (_) { }
            try {
                updateChunkSystemCamera((cameraManager as any).camera);
            } catch (e) { }
            try { if (getChunkSystem()) { processChunkRenderQueue(); } } catch (e) { }
            await new Promise((res) => requestAnimationFrame(() => requestAnimationFrame(res)));
            const url = canvas.toDataURL('image/jpeg', 0.9);
            if (prevJson) {
                try {
                    localStorage.setItem('cameraState', prevJson);
                    cameraManager.loadSavedState?.();
                    cameraManager.saveState?.();
                } catch (_) { }
            }
            // Restore preview visibility
            try { (window as any).__WE_PREVIEW_VISIBLE__ = oldPreviewVisible; } catch (_) { }
            return url;
        } catch (_) { return null; }
    };

    // Screenshot capture function - hides UI, captures canvas, saves to database
    const handleTakeScreenshot = async (): Promise<void> => {
        try {
            // Store current UI visibility state
            const prevSidebar = showBlockSidebar;
            const prevOptions = showOptionsPanel;
            const prevToolbar = showToolbar;

            // Save and hide zone visibility
            const prevZonesVisible = zoneManager.isVisible();
            if (prevZonesVisible) zoneManager.setVisible(false);

            // Save and hide block preview indicator
            const oldPreviewVisible = (window as any).__WE_PREVIEW_VISIBLE__;
            (window as any).__WE_PREVIEW_VISIBLE__ = false;

            // Hide all UI elements
            setShowBlockSidebar(false);
            setShowOptionsPanel(false);
            setShowToolbar(false);

            // Wait for UI to hide and render
            await new Promise(res => requestAnimationFrame(() => requestAnimationFrame(res)));

            // Capture screenshot
            const container = document.querySelector('.canvas-container') as HTMLElement;
            const canvas = container?.querySelector('canvas');
            if (!canvas) throw new Error('Canvas not found');

            const fullDataUrl = canvas.toDataURL('image/jpeg', 0.95);
            const thumbnailUrl = await generateThumbnail(fullDataUrl, 256, 144);

            // Save to IndexedDB
            const screenshotId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            const projectId = DatabaseManager.getCurrentProjectId();
            const screenshot = {
                id: screenshotId,
                projectId: projectId,
                dataUrl: fullDataUrl,
                timestamp: Date.now(),
                thumbnailUrl,
            };

            await DatabaseManager.saveData(
                STORES.SCREENSHOTS,
                `${projectId}::screenshot::${screenshotId}`,
                screenshot
            );

            // Restore UI
            setShowBlockSidebar(prevSidebar);
            setShowOptionsPanel(prevOptions);
            setShowToolbar(prevToolbar);

            // Restore zone visibility
            if (prevZonesVisible) zoneManager.setVisible(true);

            // Restore block preview visibility
            (window as any).__WE_PREVIEW_VISIBLE__ = oldPreviewVisible;

            // Show success notification via ToastManager
            ToastManager.showToast('Screenshot saved!');

            // Dispatch event for gallery refresh
            window.dispatchEvent(new CustomEvent('screenshot-taken'));

        } catch (error) {
            console.error('Screenshot capture failed:', error);
            // Restore UI on error
            setShowBlockSidebar(true);
            setShowOptionsPanel(true);
            setShowToolbar(true);
            // Restore zone visibility on error
            try { if (zoneManager.isVisible() === false) zoneManager.setVisible(true); } catch (_) {}
            // Restore block preview on error
            try { (window as any).__WE_PREVIEW_VISIBLE__ = true; } catch (_) {}
        }
    };

    // Load and apply saved skybox when page is loaded (one time only)
    useEffect(() => {
        if (!projectId || !pageIsLoaded) return;

        const loadSavedSkybox = async () => {
            // Add a small delay to ensure terrain builder is ready
            await new Promise(resolve => setTimeout(resolve, 1000));

            if (terrainBuilderRef.current?.changeSkybox) {
                try {
                    const savedSkybox = await DatabaseManager.getData(STORES.SETTINGS, `project:${DatabaseManager.getCurrentProjectId()}:selectedSkybox`);
                    if (typeof savedSkybox === 'string') {
                        terrainBuilderRef.current.changeSkybox(savedSkybox);
                    }
                    // Also apply saved lighting settings (ambient and directional)
                    try {
                        type LightSettings = { color?: string; intensity?: number };
                        const amb = (await DatabaseManager.getData(STORES.SETTINGS, `project:${DatabaseManager.getCurrentProjectId()}:ambientLight`)) as LightSettings | null;
                        if (amb && (typeof amb.color === 'string' || typeof amb.intensity === 'number') && terrainBuilderRef.current?.setAmbientLight) {
                            terrainBuilderRef.current.setAmbientLight({
                                color: typeof amb.color === 'string' ? amb.color : undefined,
                                intensity: typeof amb.intensity === 'number' ? amb.intensity : undefined,
                            });
                            // Update ambient light intensity for bloom threshold calculation
                            if (typeof amb.intensity === 'number') {
                                setAmbientLightIntensity(amb.intensity);
                            }
                        }
                    } catch (e) {
                        // noop
                    }
                    try {
                        type LightSettings = { color?: string; intensity?: number };
                        const dir = (await DatabaseManager.getData(STORES.SETTINGS, `project:${DatabaseManager.getCurrentProjectId()}:directionalLight`)) as LightSettings | null;
                        if (dir && (typeof dir.color === 'string' || typeof dir.intensity === 'number') && terrainBuilderRef.current?.setDirectionalLight) {
                            terrainBuilderRef.current.setDirectionalLight({
                                color: typeof dir.color === 'string' ? dir.color : undefined,
                                intensity: typeof dir.intensity === 'number' ? dir.intensity : undefined,
                            });
                        }
                    } catch (e) {
                        // noop
                    }
                } catch (error) {
                    // Error loading saved skybox
                }
            }
        };

        loadSavedSkybox();
    }, [pageIsLoaded, projectId]); // re-apply per-project

    // Load bloom settings and listen for changes
    useEffect(() => {
        const loadBloomSettings = async () => {
            try {
                const savedBloom = await DatabaseManager.getData(STORES.SETTINGS, "bloomEnabled");
                if (typeof savedBloom === "boolean") {
                    setBloomEnabled(savedBloom);
                }
            } catch (error) {
                console.error("Error loading bloom setting:", error);
            }
        };

        loadBloomSettings();

        const handleBloomChange = (e: CustomEvent) => {
            setBloomEnabled(e.detail.enabled);
        };

        window.addEventListener("bloom-settings-changed", handleBloomChange as EventListener);
        return () => {
            window.removeEventListener("bloom-settings-changed", handleBloomChange as EventListener);
        };
    }, []);

    // Listen for ambient light changes to update bloom threshold dynamically
    useEffect(() => {
        const handleAmbientChange = (e: CustomEvent) => {
            if (e.detail?.intensity !== undefined) {
                setAmbientLightIntensity(e.detail.intensity);
            }
        };
        window.addEventListener("ambient-light-changed", handleAmbientChange as EventListener);
        return () => {
            window.removeEventListener("ambient-light-changed", handleAmbientChange as EventListener);
        };
    }, []);

    useEffect(() => {
        if (!projectId) return; // Only load these once a project is open
        const loadAppSettings = async () => {
            try {
                const savedCompactMode = await DatabaseManager.getData(STORES.SETTINGS, "compactMode");
                if (savedCompactMode === false) {
                    setIsCompactMode(false);
                }

                const savedPointerLockMode = await DatabaseManager.getData(STORES.SETTINGS, "pointerLockMode");
                if (typeof savedPointerLockMode === "boolean") {
                    cameraManager.isPointerUnlockedMode = savedPointerLockMode;
                }

                const savedSensitivity = await DatabaseManager.getData(STORES.SETTINGS, "cameraSensitivity");
                if (typeof savedSensitivity === "number") {
                    cameraManager.setPointerSensitivity(savedSensitivity);
                }
            } catch (error) {
                // Error loading app settings
            }

            const savedBlockId = localStorage.getItem("selectedBlock");
            if (savedBlockId) {
                const blockId = parseInt(savedBlockId);
                if (blockId < 200) {
                    const block = [...blockTypes, ...getCustomBlocks()].find(
                        (b) => b.id === blockId
                    );
                    if (block) {
                        setCurrentBlockType(block);
                        setActiveTab("blocks");
                    }
                } else {
                    if (environmentModels && environmentModels.length > 0) {
                        const envModel = environmentModels.find(
                            (m) => m.id === blockId
                        );
                        if (envModel) {
                            setCurrentBlockType({
                                ...envModel,
                                isEnvironment: true,
                            });
                            setActiveTab("models");
                        }
                    }
                }
            }
        };

        if (!pageIsLoaded && projectId) {
            loadingManager.showLoading();
        }

        if (pageIsLoaded) {
            loadAppSettings();
            // Mark App Component Initialization as complete when page is loaded
        }
    }, [pageIsLoaded, projectId]);

    // When switching projects or returning Home, reset flags/state and clear any global player-mode leftovers
    useEffect(() => {
        if (!projectId) {
            try { loadingManager.forceHideAll(); } catch (_) { }
            setPageIsLoaded(false);
            // Drop refs so new Canvas mounts cleanly next time
            try { (terrainBuilderRef as any).current = null; } catch (_) { }
            try { (environmentBuilderRef as any).current = null; } catch (_) { }
            // Clear physics/player globals when leaving project
            try { delete (window as any).__WE_PHYSICS__; } catch (_) { }
            // Reset the camera reset tracking when leaving project
            lastResetProjectIdRef.current = null;
        } else {
            setPageIsLoaded(false);
            // Also clear tool/undo state to prevent stale bindings
            try { (undoRedoManagerRef as any).current = null; } catch (_) { }
            // Ensure orbit controls are enabled on entry; clear player-mode globals
            try {
                delete (window as any).__WE_PHYSICS__;
                delete (window as any).__WE_PLAYER_MESH__;
                delete (window as any).__WE_PLAYER_MIXER__;
                delete (window as any).__WE_PLAYER_ANIMS__;
                (window as any).__WE_CAM_KEYS__ = { left: false, right: false, up: false, down: false };
                cameraManager.setInputDisabled(false);
            } catch (_) { }
        }
    }, [projectId]);

    // Reset camera to default position when a project finishes loading
    useEffect(() => {
        if (projectId && pageIsLoaded && lastResetProjectIdRef.current !== projectId) {
            // Mark that we've reset for this project
            lastResetProjectIdRef.current = projectId;
            // Use the same reset mechanism as the settings menu button
            setCameraReset(prev => !prev);
        }
    }, [projectId, pageIsLoaded]);

    // Import pending template map after project loads (used by template creation flow)
    useEffect(() => {
        if (!pageIsLoaded || !projectId) return;
        const templateUrl = sessionStorage.getItem("pendingTemplateImport");
        if (!templateUrl) return;
        sessionStorage.removeItem("pendingTemplateImport");

        // Show loading immediately so user never sees the empty world
        loadingManager.showLoading("Loading template...", 0);

        (async () => {
            try {
                const res = await fetch(templateUrl);
                if (!res.ok) throw new Error(`Failed to fetch template: ${res.status}`);
                const blob = await res.blob();
                const file = new File([blob], "template.json", { type: "application/json" });
                await importMap(file, terrainBuilderRef, environmentBuilderRef);
            } catch (error) {
                console.error("Error importing template map:", error);
                loadingManager.hideLoading();
            }
        })();
    }, [pageIsLoaded, projectId]);

    useEffect(() => {
        const handleKeyDown = async (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "s") {
                e.preventDefault();

                setSaveStatus('saving');

                try {
                    if (terrainBuilderRef.current) {
                        await terrainBuilderRef.current.saveTerrainManually();
                        const url = await generateWorldThumbnail();
                        if (url) {
                            const pid = DatabaseManager.getCurrentProjectId();
                            await DatabaseManager.saveProjectThumbnail(pid, url);
                        }
                    }

                    if (environmentBuilderRef.current) {
                        await environmentBuilderRef.current.updateLocalStorage();
                    }
                } finally {
                    setSaveStatus('complete');
                    setTimeout(() => setSaveStatus('idle'), 2000);
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    // Block rotation keyboard shortcuts: R cycles Y rotation, T cycles face orientation
    useEffect(() => {
        const handleRotationKey = (e: KeyboardEvent) => {
            // Don't handle if typing in an input or modifier keys
            const target = e.target as HTMLElement;
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
            if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;

            if (e.key === 'r') {
                e.preventDefault();
                // R: cycle Y rotation (0 → 90 → 180 → 270 → 0)
                setCurrentRotationIndex(prev => getNextYRotation(prev));
            } else if (e.key === 't') {
                e.preventDefault();
                // T: cycle face orientation
                setCurrentRotationIndex(prev => getNextFaceRotation(prev));
            }
        };
        window.addEventListener('keydown', handleRotationKey);
        return () => window.removeEventListener('keydown', handleRotationKey);
    }, []);

    useEffect(() => {
        const disableTabbing = (e) => {
            if (e.key === "Tab") {
                e.preventDefault();
            }
        };

        window.addEventListener("keydown", disableTabbing);

        return () => {
            window.removeEventListener("keydown", disableTabbing);
        };
    }, []);

    useEffect(() => {
        if (!projectId) return; // Only initialize when a project is open
        DatabaseManager.saveData(STORES.UNDO, "states", []);
        DatabaseManager.saveData(STORES.REDO, "states", []);
    }, [projectId]);

    // Crosshair is always visible in crosshair mode, no polling needed

    useEffect(() => {
        const shouldDefocus = (el: HTMLElement | null) => {
            if (!el) return false;
            const tag = el.tagName;
            if (tag === "BUTTON") return true;
            if (tag === "INPUT") {
                const input = el as HTMLInputElement;
                return input.type === "range" || input.type === "checkbox";
            }
            return false;
        };

        const defocusHandler = (e: Event) => {
            const target = e.target as HTMLElement | null;
            if (shouldDefocus(target)) {
                // Use a micro-delay so default click behaviour executes first
                setTimeout(() => target?.blur(), 0);
            }
        };

        window.addEventListener("click", defocusHandler, true);
        window.addEventListener("focusin", defocusHandler, true);

        return () => {
            window.removeEventListener("click", defocusHandler, true);
            window.removeEventListener("focusin", defocusHandler, true);
        };
    }, []);

    // Removed: TerrainBuilder ref call to setCurrentBlockType (not an imperative handle)
    // useEffect(() => {
    //     if (terrainBuilderRef.current)
    //         terrainBuilderRef.current.setCurrentBlockType(currentBlockType);
    // }, [currentBlockType]);

    // Initialize physics manager lazy when toggled on
    const ensurePhysics = () => {
        if (!physicsRef.current) {
            physicsRef.current = new PhysicsManager({ gravity: { x: 0, y: -32, z: 0 }, tickRate: 60 });
        }
        return physicsRef.current!;
    };

    useEffect(() => {
        if (!playerModeEnabled) {
            return;
        }
        const stateObj = (window as any).__WE_INPUT_STATE__ || { state: {} };
        (window as any).__WE_INPUT_STATE__ = stateObj;
        const allowed: Record<string, boolean> = { w: true, a: true, s: true, d: true, sp: true, sh: true, c: true };
        const mapKey = (e: KeyboardEvent): string | null => {
            const k = e.key.toLowerCase();
            if (k === ' ') return 'sp';
            if (k === 'shift') return 'sh';
            if (k === 'w' || k === 'a' || k === 's' || k === 'd' || k === 'c') return k;
            return null;
        };
        const onKeyDown = (e: KeyboardEvent) => {
            const k = mapKey(e);
            if (!k || !allowed[k]) return;
            stateObj.state[k] = true;
        };
        const onKeyUp = (e: KeyboardEvent) => {
            const k = mapKey(e);
            if (!k || !allowed[k]) return;
            stateObj.state[k] = false;
        };
        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('keyup', onKeyUp);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('keyup', onKeyUp);
        };
    }, [playerModeEnabled]);

    // Update physics ground plane when baseGridY changes while in player mode
    useEffect(() => {
        if (!playerModeEnabled || !physicsRef.current) {
            lastBaseGridYRef.current = null; // Reset when player mode is disabled
            return;
        }

        const updateGround = async () => {
            const physics = physicsRef.current;
            if (!physics) return;

            const baseGridY = terrainBuilderRef.current?.getGridY?.() ?? 0;

            // Only update if baseGridY actually changed
            if (lastBaseGridYRef.current !== null && lastBaseGridYRef.current === baseGridY) {
                return;
            }

            lastBaseGridYRef.current = baseGridY;
            const groundY = baseGridY - 0.5; // Grid is at baseGridY - 0.5, matching raycast behavior

            // Ensure physics is ready before updating ground
            await physics.ready();
            // Update ground position synchronously to avoid race conditions
            physics.updateGroundY(groundY);
        };

        // Update immediately
        updateGround();

        // Also check periodically in case baseGridY changes externally (e.g., via SettingsMenu)
        const interval = setInterval(updateGround, 500);

        return () => {
            clearInterval(interval);
        };
    }, [playerModeEnabled]);

    const togglePlayerMode = () => {
        const next = !playerModeEnabled;
        setPlayerModeEnabled(next);
        try {
            cameraManager.setInputDisabled(next);
        } catch (_) { }
        if (next) {
            const physics = ensurePhysics();
            (window as any).__WE_PHYSICS__ = physics;
            (window as any).__WE_INPUT_STATE__ = (window as any).__WE_INPUT_STATE__ || { state: {} };
            // Initialize player-mode camera globals so first entry works without an arrow key press
            (window as any).__WE_CAM_KEYS__ = (window as any).__WE_CAM_KEYS__ || { left: false, right: false, up: false, down: false };
            (window as any).__WE_CAM_OFFSET_RADIUS__ = (window as any).__WE_CAM_OFFSET_RADIUS__ ?? 8.0;
            (window as any).__WE_CAM_OFFSET_HEIGHT__ = (window as any).__WE_CAM_OFFSET_HEIGHT__ ?? 3.0;
            // Let TerrainBuilder compute yaw from camera on first animate tick; provide fallback here
            (window as any).__WE_CAM_OFFSET_YAW__ = (window as any).__WE_CAM_OFFSET_YAW__ ?? (cameraManager.camera?.rotation?.y || 0);
            // No pointer lock
            physics.ready().then(() => {
                // Get baseGridY from TerrainBuilder, default to 0 if not available
                const baseGridY = terrainBuilderRef.current?.getGridY?.() ?? 0;
                const groundY = baseGridY - 0.5; // Grid is at baseGridY - 0.5, matching raycast behavior
                physics.addFlatGround(4000, groundY);
                const pos = cameraPosition ?? { x: 0, y: 10, z: 0 } as any;
                physics.createOrResetPlayer(new Vector3(pos.x ?? 0, pos.y ?? 10, pos.z ?? 0));
                // Register entity colliders for all entities with addCollider enabled
                if (environmentBuilderRef.current?.registerAllEntityColliders) {
                    environmentBuilderRef.current.registerAllEntityColliders();
                }
            });
        } else {
            // Clear all entity colliders before disabling physics
            if (environmentBuilderRef.current?.clearAllEntityColliders) {
                environmentBuilderRef.current.clearAllEntityColliders();
            }
            try { delete (window as any).__WE_PHYSICS__; } catch (_) { }
            // Reset solid query flag so it can be set up again when player mode is re-enabled
            try { delete (window as any).__WE_SOLID_BOUND__; } catch (_) { }
            try { delete (window as any).__WE_IS_SOLID__; } catch (_) { }
            // Despawn player glTF
            try {
                const scene: any = (window as any).__WE_SCENE__;
                const mesh: any = (window as any).__WE_PLAYER_MESH__;
                if (scene && mesh) { scene.remove(mesh); }
                (window as any).__WE_PLAYER_MESH__ = undefined;
                (window as any).__WE_PLAYER_MIXER__ = undefined;
                (window as any).__WE_PLAYER_ANIMS__ = undefined;
                (window as any).__WE_PLAYER_ACTIVE__ = undefined;
            } catch (_) { }
            // No pointer lock exit
        }
    };

    const handleToggleCompactMode = async () => {
        const newCompactValue = !isCompactMode;
        setIsCompactMode(newCompactValue);
        try {
            await DatabaseManager.saveData(STORES.SETTINGS, "compactMode", newCompactValue);
        } catch (error) {
            // Error saving compact mode setting
        }
    };

    const LoadingScreen = () => (
        <div className="loading-screen">
            <img
                src={"assets/img/hytopia_logo_white.png"}
                alt="Hytopia Logo"
                className="loading-logo"
            />
            <div className="loading-spinner"></div>
            <div className="loading-text">
                <i>Loading...</i>
            </div>
            <div className="version-text">HYTOPIA Map Builder v{version}</div>
        </div>
    );

    const handleTextureReady = async (faceTextures, textureName) => {
        try {
            const faceMap = {
                top: "+y",
                bottom: "-y",
                left: "-x",
                right: "+x",
                front: "+z",
                back: "-z",
            };

            const newBlockData = {
                name:
                    textureName
                        .replace(/[^a-zA-Z0-9_\-\s]/g, "")
                        .replace(/\s+/g, "_") || "custom_texture",
                textureUri: faceTextures.all || faceTextures.top || null,
                sideTextures: {},
                isCustom: true,
                isMultiTexture: false,
            };

            let hasSpecificFaces = false;
            for (const face in faceTextures) {
                if (face !== "all" && faceTextures[face] && faceMap[face]) {
                    const coordinateKey = faceMap[face];
                    newBlockData.sideTextures[coordinateKey] =
                        faceTextures[face];
                    hasSpecificFaces = true;
                }
            }

            if (!hasSpecificFaces && faceTextures.all) {
                newBlockData.sideTextures["+y"] = faceTextures.all;
            } else if (
                hasSpecificFaces &&
                !newBlockData.sideTextures["+y"] &&
                newBlockData.textureUri
            ) {
                newBlockData.sideTextures["+y"] = newBlockData.textureUri;
            }

            newBlockData.isMultiTexture = hasSpecificFaces;

            if (!newBlockData.textureUri && hasSpecificFaces) {
                newBlockData.textureUri = newBlockData.sideTextures["+y"];
            }

            await processCustomBlock(newBlockData);

            try {
                const updatedCustomBlocks = getCustomBlocks();
                await DatabaseManager.saveData(
                    STORES.CUSTOM_BLOCKS,
                    "blocks",
                    updatedCustomBlocks
                );
            } catch (dbError) {
                // Error saving custom blocks after texture generation
            }

            refreshBlockTools();
        } catch (error) {
            // Error processing generated texture
        }
    };

    const handleGetAvailableBlocks = useCallback(() => {
        try {
            return getHytopiaBlocks();
        } catch (error) {
            return [];
        }
    }, []);

    const handleGetAvailableEntities = useCallback(() => {
        try {
            return environmentModels.map(model => ({
                name: model.name,
                displayName: model.name
                    .split("-")
                    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
                    .join(" "),
                modelUrl: model.modelUrl
            }));
        } catch (error) {
            return [];
        }
    }, []);

    const handleLoadAISchematic = useCallback((schematic) => {
        terrainBuilderRef.current?.activateTool("schematic", schematic);
    }, []);

    const handleUpdateBlockName = async (blockId: number, newName: string) => {
        try {
            const success = await updateCustomBlockName(blockId, newName);
            if (!success) {
                throw new Error("BlockTypesManager failed to update name.");
            }
            const updatedBlocks = getCustomBlocks();
            await DatabaseManager.saveData(STORES.CUSTOM_BLOCKS, 'blocks', updatedBlocks);

            if (currentBlockType?.id === blockId) {
                setCurrentBlockType(prev => ({ ...prev, name: newName }));
            }
            refreshBlockTools();
        } catch (error) {
            alert(`Failed to rename block: ${error.message || "Unknown error"}`);
            throw error;
        }
    };

    const handleDownloadBlock = async (blockType: any) => {
        if (!blockType) return;
        const zip = new JSZip();
        const faceKeys = ["+x", "-x", "+y", "-y", "+z", "-z"];
        const textures = blockType.sideTextures || {};
        const mainTexture = blockType.textureUri;
        let hasError = false;

        for (const key of faceKeys) {
            const dataUrl = textures[key] || mainTexture;
            let blob: Blob | null = null;

            if (dataUrl && dataUrl.startsWith('data:image')) {
                blob = dataURLtoBlob(dataUrl);
            } else if (dataUrl && (dataUrl.startsWith('./') || dataUrl.startsWith('/'))) {
                try {
                    const response = await fetch(dataUrl);
                    if (response.ok) {
                        blob = await response.blob();
                    }
                } catch (fetchError) {
                    // Error fetching texture
                }
            }

            if (!blob) {
                try {
                    blob = await createPlaceholderBlob();
                    if (!blob) {
                        hasError = true; continue;
                    }
                } catch (placeholderError) {
                    hasError = true; continue;
                }
            }
            const fileName = `${key}.png`;
            zip.file(fileName, blob);
        }
        if (hasError) alert("Warning: Some textures missing/invalid; placeholders used or skipped. Check console.");
        try {
            const zipBlob = await zip.generateAsync({ type: "blob" });
            saveAs(zipBlob, `${blockType.name}.zip`);
        } catch (err) {
            alert("Failed to save zip.");
        }
    };

    const handleDeleteBlock = async (blockType: any) => {
        if (!blockType || !blockType.isCustom) return;
        const confirmMessage = `Deleting "${blockType.name}" (ID: ${blockType.id}) cannot be undone. Instances of this block in the world will be lost. Are you sure?`;
        if (window.confirm(confirmMessage)) {
            try {
                removeCustomBlock(blockType.id);
                const updatedBlocks = getCustomBlocks();
                await DatabaseManager.saveData(STORES.CUSTOM_BLOCKS, 'blocks', updatedBlocks);
                const errorId = 0;
                const currentTerrain = await DatabaseManager.getData(STORES.TERRAIN, "current") || {};
                let blocksReplaced = 0;
                const newTerrain = Object.entries(currentTerrain).reduce((acc, [pos, id]) => {
                    if (id === blockType.id) {
                        acc[pos] = errorId; blocksReplaced++;
                    } else {
                        acc[pos] = id;
                    }
                    return acc;
                }, {});
                if (blocksReplaced > 0) {
                    await DatabaseManager.saveData(STORES.TERRAIN, "current", newTerrain);
                    terrainBuilderRef.current?.buildUpdateTerrain();
                }
                refreshBlockTools();
                if (currentBlockType?.id === blockType.id) {
                    setCurrentBlockType(blockTypes[0]);
                    setActiveTab('blocks');
                    localStorage.setItem("selectedBlock", blockTypes[0].id.toString());
                }
            } catch (error) {
                alert(`Failed to delete block: ${error.message}`);
            }
        }
    };

    const handleConvertComponentToEntity = async (component: any) => {
        if (!component?.schematic) {
            alert("No component selected or component has no schematic.");
            return;
        }

        const componentName = component.name || component.prompt || "Component";
        const sanitizedName = componentName
            .replace(/[^a-zA-Z0-9_\-\s]/g, "")
            .replace(/\s+/g, "_")
            .substring(0, 64) || "converted_component";

        try {
            // Convert the component blocks to GLTF
            const schematic = component.schematic.blocks ? component.schematic : { blocks: component.schematic };
            const result = await convertComponentToGLTF(schematic, sanitizedName);

            if (!result.success || !result.data) {
                throw new Error(result.error || "Failed to convert component to GLTF");
            }

            // Get existing custom models
            const existingModels = (await DatabaseManager.getData(STORES.CUSTOM_MODELS, "models")) || [];

            // Check for duplicate names
            const existingNames = new Set([
                ...(existingModels as any[]).map((m: any) => m.name.toLowerCase()),
                ...environmentModels.map((m) => m.name.toLowerCase())
            ]);

            let finalName = sanitizedName;
            let counter = 1;
            while (existingNames.has(finalName.toLowerCase())) {
                finalName = `${sanitizedName}_${counter}`;
                counter++;
            }

            // Save to custom models database
            const modelDataForDB = {
                name: finalName,
                data: result.data,
                timestamp: Date.now(),
            };

            const updatedModelsForDB = [...(existingModels as any[]), modelDataForDB];
            await DatabaseManager.saveData(STORES.CUSTOM_MODELS, "models", updatedModelsForDB);

            // Create a blob URL for immediate use
            const blob = new Blob([result.data], { type: "model/gltf+json" });
            const fileUrl = URL.createObjectURL(blob);

            // Register in environmentModels for immediate availability
            const newEnvironmentModel = {
                id: Math.max(
                    4999,
                    ...environmentModels
                        .filter((model) => model.isCustom)
                        .map((model) => model.id)
                ) + 1,
                name: finalName,
                modelUrl: fileUrl,
                thumbnailUrl: null,
                isEnvironment: true,
                isCustom: true,
                category: "Custom",
                animations: ["idle"],
                addCollider: true,
            };

            environmentModels.push(newEnvironmentModel);

            // Load the model into the environment builder
            if (environmentBuilderRef.current?.loadModel) {
                try {
                    await environmentBuilderRef.current.loadModel(fileUrl);
                } catch (loadError) {
                    console.error(`Error loading converted model into environment:`, loadError);
                }
            }

            // Refresh the sidebar to show the new model
            refreshBlockTools();

            // Show success message
            alert(`Component "${componentName}" has been converted to entity "${finalName}"!\n\nYou can now find it in the Models tab under "Custom" category.`);

            // Optionally switch to models tab
            setActiveTab("models");

        } catch (error) {
            console.error("Failed to convert component to entity:", error);
            alert(`Failed to convert component: ${(error as Error).message}`);
        }
    };

    return (
        <Provider theme={defaultTheme}>
            <div className="App">
                {!projectId && (
                    <ProjectHome onOpen={(id) => { try { DatabaseManager.setCurrentProjectId(id); } catch (_) { } setProjectId(id); }} />
                )}
                {IS_UNDER_CONSTRUCTION && <UnderConstruction />}

                {projectId && !pageIsLoaded && <LoadingScreen />}

                <GlobalLoadingScreen />

                {/* Live selection dimensions tip */}
                <SelectionDimensionsTip />

                {/* Show QuickTips only on web (not Electron) and after initial load */}
                {!isElectronRuntime() && pageIsLoaded && <QuickTips />}

                {projectId && (
                    <UndoRedoManager
                        ref={undoRedoManagerRef}
                        terrainBuilderRef={terrainBuilderRef}
                        environmentBuilderRef={environmentBuilderRef}
                    />
                )}

                {projectId && showBlockSidebar && (
                    <BlockToolsSidebar
                        isCompactMode={isCompactMode}
                        onOpenTextureModal={() => setIsTextureModalOpen(true)}
                        terrainBuilderRef={terrainBuilderRef}
                        activeTab={activeTab}
                        onLoadSchematicFromHistory={handleLoadAISchematic}
                        setActiveTab={setActiveTab}
                        setCurrentBlockType={setCurrentBlockType}
                        environmentBuilder={environmentBuilderRef.current}
                        onPlacementSettingsChange={setPlacementSettings}
                        setPlacementSize={setPlacementSize}
                        currentRotationIndex={currentRotationIndex}
                        setCurrentRotationIndex={setCurrentRotationIndex}
                        currentShapeType={currentShapeType}
                        setCurrentShapeType={setCurrentShapeType}
                    />
                )}

                {projectId && showOptionsPanel && (
                    <BlockToolOptions
                        totalEnvironmentObjects={totalEnvironmentObjects}
                        terrainBuilderRef={terrainBuilderRef}
                        onResetCamera={() => setCameraReset(prev => !prev)}
                        onTakeScreenshot={handleTakeScreenshot}
                        activeTab={activeTab}
                        selectedBlock={currentBlockType}
                        onUpdateBlockName={handleUpdateBlockName}
                        onDownloadBlock={handleDownloadBlock}
                        onDeleteBlock={handleDeleteBlock}
                        placementSettings={placementSettings}
                        onPlacementSettingsChange={setPlacementSettings}
                        isCompactMode={isCompactMode}
                        onToggleCompactMode={handleToggleCompactMode}
                        showAIComponents={isAIComponentsActive}
                        getAvailableBlocks={handleGetAvailableBlocks}
                        getAvailableEntities={handleGetAvailableEntities}
                        loadAISchematic={handleLoadAISchematic}
                        onConvertComponentToEntity={handleConvertComponentToEntity}
                        currentRotationIndex={currentRotationIndex}
                        currentShapeType={currentShapeType}
                    />
                )}

                {projectId && (
                    <TextureGenerationModal
                        isOpen={isTextureModalOpen}
                        onClose={() => setIsTextureModalOpen(false)}
                        onTextureReady={handleTextureReady}
                    />
                )}

                {/* Global modal for schematic naming (triggered from SelectionTool) */}
                <SchematicNameModal />

                {projectId && <div className="vignette-gradient"></div>}

                {projectId && toasts.length > 0 && (
                    <div
                        style={{
                            position: "fixed",
                            top: "40px",
                            left: "50%",
                            transform: "translateX(-50%)",
                            zIndex: 10000,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "8px",
                            pointerEvents: "none",
                        }}
                    >
                        {toasts.map((toast, index) => (
                            <div
                                key={toast.id}
                                className="toast-notification"
                                style={{
                                    backgroundColor: "rgba(0, 0, 0, 0.8)",
                                    color: "white",
                                    padding: "8px 16px",
                                    borderRadius: "4px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
                                    fontFamily: "Arial, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: "bold",
                                    animation: "slideUpIn 0.3s ease-out",
                                }}
                            >
                                {toast.message}
                            </div>
                        ))}
                    </div>
                )}

                {projectId && saveStatus !== 'idle' && (
                    <div
                        style={{
                            position: "fixed",
                            bottom: "80px",
                            left: "50%",
                            transform: "translateX(-50%)",
                            backgroundColor: "rgba(0, 0, 0, 0.8)",
                            color: "white",
                            padding: "8px 16px",
                            borderRadius: "4px",
                            zIndex: 9999,
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
                            fontFamily: "Arial, sans-serif",
                            fontSize: "14px",
                            fontWeight: "bold",
                            pointerEvents: "none",
                        }}
                    >
                        {saveStatus === 'saving' ? (
                            <>
                                <div
                                    style={{
                                        width: "16px",
                                        height: "16px",
                                        borderRadius: "50%",
                                        border: "3px solid rgba(255, 255, 255, 0.3)",
                                        borderTopColor: "white",
                                        animation: "spin 1s linear infinite",
                                    }}
                                />
                                Saving...
                            </>
                        ) : (
                            <>
                                <div
                                    style={{
                                        width: "16px",
                                        height: "16px",
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    ✓
                                </div>
                                Save complete!
                            </>
                        )}
                    </div>
                )}

                {projectId && (
                    <Canvas
                        flat  // Disables automatic sRGB encoding and tone mapping - we handle this via PostProcessingManager
                        key={projectId}
                        shadows
                        className="canvas-container"
                        gl={contextAttributes}
                        camera={{ fov: 75, near: 0.1, far: 1000 }}
                        onCreated={({ gl }) => {
                            // Match SDK renderer configuration exactly to prevent bloom washout
                            gl.outputColorSpace = SRGBColorSpace;
                            gl.toneMapping = NoToneMapping;
                        }}
                    >
                        <TerrainBuilder
                            key={`tb-${projectId}`}
                            isInputDisabled={isTextureModalOpen}
                            ref={terrainBuilderRef}
                            currentBlockType={currentBlockType}
                            setCurrentBlockType={setCurrentBlockType}
                            mode={mode}
                            axisLockEnabled={axisLockEnabled}
                            placementSize={placementSize}
                            cameraReset={cameraReset}
                            cameraAngle={cameraAngle}
                            setPageIsLoaded={setPageIsLoaded}
                            onSceneReady={(sceneObject) => { setScene(sceneObject); }}
                            gridSize={gridSize}
                            environmentBuilderRef={environmentBuilderRef}
                            previewPositionToAppJS={setCurrentPreviewPosition}
                            undoRedoManager={undoRedoManagerRef}
                            customBlocks={getCustomBlocks()}
                            snapToGrid={placementSettings.snapToGrid}
                            onCameraPositionChange={setCameraPosition}
                            currentRotationIndex={currentRotationIndex}
                            currentShapeType={currentShapeType}
                        />
                        <EnvironmentBuilder
                            key={`eb-${projectId}`}
                            ref={environmentBuilderRef}
                            scene={scene}
                            projectId={projectId}
                            currentBlockType={currentBlockType}
                            onTotalObjectsChange={setTotalEnvironmentObjects}
                            placementSize={placementSize}
                            previewPositionFromAppJS={currentPreviewPosition}
                            placementSettings={placementSettings}
                            onPlacementSettingsChange={setPlacementSettings}
                            undoRedoManager={undoRedoManagerRef}
                            terrainBuilderRef={terrainBuilderRef}
                            cameraPosition={cameraPosition}
                        />
                        {bloomEnabled && (
                            <PostProcessingManager
                                enabled={bloomEnabled}
                                bloomStrength={0.5}
                                bloomRadius={0.4}
                                ambientLightIntensity={ambientLightIntensity}
                            />
                        )}
                    </Canvas>
                )}

                {/* Desktop app CTA removed in favor of QuickTips when on web */}

                {projectId && showToolbar && (
                    <ToolBar
                        terrainBuilderRef={terrainBuilderRef}
                        environmentBuilderRef={environmentBuilderRef}
                        mode={mode}
                        handleModeChange={setMode}
                        axisLockEnabled={axisLockEnabled}
                        setAxisLockEnabled={setAxisLockEnabled}
                        placementSize={placementSize}
                        setPlacementSize={setPlacementSize}
                        undoRedoManager={undoRedoManagerRef}
                        currentBlockType={currentBlockType}
                        onOpenTextureModal={() => setIsTextureModalOpen(true)}
                        toggleAIComponents={() => setIsAIComponentsActive((v) => !v)}
                        isAIComponentsActive={isAIComponentsActive}
                        setIsSaving={setSaveStatus}
                        activeTab={activeTab}
                        playerModeEnabled={playerModeEnabled}
                        onTogglePlayerMode={togglePlayerMode}
                        onSwitchProject={handleSwitchProject}
                        onTakeScreenshot={handleTakeScreenshot}
                    />
                )}

                {/* Crosshair visible only in crosshair mode */}
                {projectId && !cameraManager.isPointerUnlockedMode && (
                    <div
                        style={{
                            position: "fixed",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: "20px",
                            height: "20px",
                            pointerEvents: "none",
                            zIndex: 10000,
                        }}
                    >
                        <div
                            style={{
                                position: "absolute",
                                left: "50%",
                                top: "0",
                                width: "2px",
                                height: "100%",
                                background: "#ffffff",
                                transform: "translateX(-50%)",
                            }}
                        />
                        <div
                            style={{
                                position: "absolute",
                                top: "50%",
                                left: "0",
                                width: "100%",
                                height: "2px",
                                background: "#ffffff",
                                transform: "translateY(-50%)",
                            }}
                        />
                    </div>
                )}

                {/* <button
                    className="toolbar-button"
                    onClick={async () => await DatabaseManager.clearDatabase()}
                    title="Clear Database"
                    style={{
                        position: "absolute",
                        bottom: "10px",
                        left: "10px",
                    }}
                >
                    <FaDatabase />
                </button> */}
            </div>
        </Provider>
    );
}

export default App;
